<script setup>
import { ref, computed } from 'vue';
import IstogramGraph from '~/components/Filters/IstogramGraph.vue';
// import component
import 'vue-slider-component/dist-css/vue-slider-component.css';
import VueSlider from 'vue-slider-component/dist-css/vue-slider-component.umd.min.js';

// import theme
import 'vue-slider-component/theme/default.css';

const props = defineProps({
  title: {
    type: String,
    required: true,
  },
  rangeTooltip: {
    type: String,
    required: true,
  },
  min: {
    type: Number,
    required: true,
  },
  selectedMin: {
    type: Number,
    required: true,
  },
  max: {
    type: Number,
    required: true,
  },
  selectedMax: {
    type: Number,
    required: true,
  },
  buckets: {
    type: Object,
    required: true,
  },
  currency: {
    type: String,
    required: true,
  },
  formatter: {
    required: true,
  },
});

const emit = defineEmits(['newSliderRange']);

let sliderRef = ref(null);
const vmodel = ref(null);

// long currency multiplier
const multiplier = computed(() => {
  if (['KRW', 'ARS', 'COP', 'CLP'].includes(props.currency)) return 1000;
  if (['JPY', 'INR', 'RUB'].includes(props.currency)) return 100;
  if (['MXN'].includes(props.currency)) return 10;
  return 1;
});

const createPoints = (min, max) => {
  const newPoints = [];
  const range = max - min;

  const numberOfSteps = Math.ceil(Math.log10(range + 1) * 10);

  const logMin = min ? Math.log(min) : 0;
  const logMax = Math.log(max);

  const step = (logMax - logMin) / numberOfSteps;

  for (let i = 0; i <= numberOfSteps; i++) {
    const value = Math.exp(logMin + i * step);
    const nextValue = Math.exp(logMin + (i + 1) * step);
    // Ensure step is at least 1 otherwise it will be 0. Happens when range tehre is a largeee difference between min and max
    const stepSize = nextValue - value >= 1 ? nextValue - value : 1;

    newPoints.push({
      value: value,
      step: stepSize,
    });
  }

  // Ensure min and max are included
  newPoints[0].value = min;
  newPoints[newPoints.length - 1].value = max;

  return newPoints.map((el) => {
    return {
      value: Math.floor(el.value * multiplier.value),
      step: Math.floor(el.step * multiplier.value),
    };
  });
};

const points = createPoints(props.min, props.max);

const sliderData = computed(() => {
  let result = [];

  points.forEach((point, idx) => {
    const lastPointValue = points[points.length - 1].value;

    if (point.value === lastPointValue || point.value >= props.max) {
      return;
    } else {
      const nextPoint = points[idx + 1];

      for (let i = point.value; i <= nextPoint.value; i += point.step) {
        if (i >= props.max) {
          result.push(props.max);
          break;
        }
        result.push(i);
      }
    }
  });

  const uniqueValues = new Set(result);

  return [...uniqueValues];
});

const sliderMax = computed(() => {
  return sliderData.value[sliderData.value.length - 1];
});

const sliderMin = computed(() => {
  let firstPoint = sliderData.value[0];

  for (const element of sliderData.value) {
    if (element > props.min) break;
    firstPoint = element;
  }

  return firstPoint;
});

watch(
  [() => props.selectedMin, () => props.selectedMax],
  ([newMin, newMax]) => {
    vmodel.value = [newMin === props.min ? sliderMin.value : newMin, newMax === props.max ? sliderMax.value : newMax];
  },
  { immediate: true },
);

function sliderCallback() {
  let sliderVal = sliderRef.value.getValue();
  emit('newSliderRange', { min: sliderVal[0], max: sliderVal[1] });
}
</script>

<template>
  <div class="flex flex-col mb-5">
    <div class="mb-4 text-left">
      <span class="text-left text-2xl font-semibold text-zinc-900 dark:text-white">{{ title }}</span>
      <br />
      <span class="text-md text-left font-normal mb-2" id="priceMinMax">{{ rangeTooltip }}</span>
    </div>
    <div class="relative px-4 md:px-0">
      <IstogramGraph
        class="px-6 md:px-0"
        :points="sliderData"
        :buckets="buckets"
        :selectedMinPrice="vmodel[0]"
        :selectedMaxPrice="vmodel[1]"
      />
      <vue-slider
        ref="sliderRef"
        v-model="vmodel"
        :min="min"
        :max="sliderMax"
        :data="sliderData"
        :enable-cross="false"
        :tooltip-formatter="(val) => formatter(val)"
        :dot-size="14"
        :process-style="{ backgroundColor: '#008ec8', position: 'absolute', top: '0' }"
        :rail-style="{ backgroundColor: 'lightgray', height: '3px', position: 'absolute', top: '-2px' }"
        @dragEnd="sliderCallback"
      ></vue-slider>
    </div>
  </div>
</template>

<style scoped></style>
